<div class="main-view space-y-2 md:space-y-4">
    <app-page-surface-header titleIcon="dashboard"
        title="Dashboard"
        subtitle="Platform overview - agents, clients, and PD activity across the estate."
    />

    <!-- Tenant Filter Bar -->
    <div class="flex flex-wrap items-center gap-3 p-4 panel rounded-2xl">
        @if (canViewTenantFilter) {
        <label for="dashboard-view-select" class="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">View:</label>
        <select
            id="dashboard-view-select"
            class="form-select w-auto min-w-[160px] text-sm rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900"
            [(ngModel)]="selectedTenantId"
            (ngModelChange)="onTenantChange()">
            <option [ngValue]="undefined">All Tenants</option>
            <option *ngFor="let t of tenants" [ngValue]="t.id">{{ t.tenant_name }}</option>
        </select>
        }

        <label for="dashboard-period-select" class="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap ml-2">Period:</label>
        <select
            id="dashboard-period-select"
            class="form-select w-auto min-w-[140px] text-sm rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900"
            [(ngModel)]="selectedPeriod"
            (ngModelChange)="onPeriodChange($event)">
            <option value="7d">Last 7 days</option>
            <option value="15d">Last 15 days</option>
            <option value="30d">Last 30 days</option>
        </select>

        <span *ngIf="stats?.cache_generated_at && !selectedTenantId" class="text-xs text-gray-400 ml-auto">
            Refreshed {{ stats!.cache_generated_at | date:'HH:mm' }}
        </span>
    </div>

    <!-- KPI Cards -->
    <div class="grid gap-2 grid-cols-1 md:grid-cols-3 md:gap-4" [ngClass]="kpiGridClass">

        @if (canViewAgentKpis) {
        <app-dashboard-sub-module
            [moduleName]="'Active Agents'"
            [moduleCount]="isLoading ? '...' : kpiActiveAgents"
            [percentageValue]="isLoading ? '' : kpiActiveAgentsBadge"
            [badgeSuffix]="''"
            [tagLabel]="'This month'">
            <span class="moduleIcon bg-white dark:bg-gray-900 p-2 rounded-2xl">
                <img src="/assets/images/chatbot-speech-bubble@2x.png" class="size-8" alt="" />
            </span>
        </app-dashboard-sub-module>
        }

        @if (canViewPdData) {
        <app-dashboard-sub-module
            [moduleName]="'PD Volume Today'"
            [moduleCount]="isLoading ? '...' : kpiPdToday"
            [percentageValue]="isLoading ? '' : kpiPdTodayTrend"
            [tagLabel]="'Since yesterday'">
            <span class="moduleIcon bg-white dark:bg-gray-900 p-2 rounded-2xl">
                <img src="/assets/images/done@2x.png" class="size-8" alt="" />
            </span>
        </app-dashboard-sub-module>
        }

        @if (canViewAgentKpis) {
        <app-dashboard-sub-module
            [moduleName]="'Agent Accuracy%'"
            [moduleCount]="isLoading ? '...' : kpiAccuracy"
            [percentageValue]="isLoading ? '' : kpiAccuracyTrend"
            [tagLabel]="'This week'">
            <span class="moduleIcon bg-white dark:bg-gray-900 p-2 rounded-2xl">
                <img src="/assets/images/accuracy@2x.png" class="size-8" alt="" />
            </span>
        </app-dashboard-sub-module>
        }

        @if (canViewPdData) {
        <app-dashboard-sub-module
            [moduleName]="'Pending Policy Reviews'"
            [moduleCount]="isLoading ? '...' : kpiPendingReviews"
            [percentageValue]="isLoading ? '' : kpiPendingReviewsTrend"
            [tagLabel]="'from yesterday'">
            <span class="moduleIcon bg-white dark:bg-gray-900 p-2 rounded-2xl">
                <img src="/assets/images/file@2x.png" class="size-8" alt="" />
            </span>
        </app-dashboard-sub-module>
        }

        @if (canViewPdData) {
        <app-dashboard-sub-module
            [moduleName]="'Average Time'"
            [moduleCount]="isLoading ? '...' : kpiAvgTime"
            [percentageValue]="isLoading ? '' : kpiAvgTimeTrend"
            [tagLabel]="'vs Last Week'">
            <span class="moduleIcon bg-white dark:bg-gray-900 p-2 rounded-2xl">
                <img src="/assets/images/clock1.png" class="size-8" alt="" />
            </span>
        </app-dashboard-sub-module>
        }
    </div>

    <!-- Own row (wrapped: a custom element is inline, so space-y would not separate it). -->
    <div><app-platform-health-card /></div>

    <div class="grid gap-4 xl:grid-cols-12">
        <!-- Reclaims the sidebar's width when the activity panel is hidden. -->
        <div [ngClass]="canViewRecentActivity ? 'xl:col-span-9' : 'xl:col-span-12'">
            <div class="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-2">
                <!-- Weekly Activity Chart -->
                @if (canViewPdData) {
                <div>
                    <app-card-outer>
                        <app-card-title [cardHead]="'PD Sessions Over Time'" [headingIcon]="'list'" />
                        <div>
                            <apx-chart
                                *ngIf="weeklyActivityChart && !isLoading"
                                [series]="weeklyActivityChart.series"
                                [chart]="weeklyActivityChart.chart"
                                [xaxis]="weeklyActivityChart.xaxis"
                                [yaxis]="weeklyActivityChart.yaxis"
                                [plotOptions]="weeklyActivityChart.plotOptions"
                                [dataLabels]="weeklyActivityChart.dataLabels"
                                [stroke]="weeklyActivityChart.stroke"
                                [colors]="weeklyActivityChart.colors"
                                [grid]="weeklyActivityChart.grid"
                                [fill]="weeklyActivityChart.fill"
                                [tooltip]="weeklyActivityChart.tooltip">
                            </apx-chart>
                            <div *ngIf="isLoading" class="h-[300px] flex items-center justify-center text-gray-400 text-sm">Loading...</div>
                        </div>
                    </app-card-outer>
                </div>
                }

                <!-- PD Status Breakdown Donut -->
                @if (canViewPdData) {
                <div>
                    <app-card-outer>
                        <app-card-title [headingIcon]="'clipboard'" [cardHead]="'PD Status Breakdown'"/>
                        <div class="flex items-center justify-between p-4">
                            <div class="w-1/2">
                                <apx-chart
                                    *ngIf="pdStatusChart && !isLoading"
                                    [series]="pdStatusChart.series"
                                    [chart]="pdStatusChart.chart"
                                    [labels]="pdStatusChart.labels"
                                    [colors]="pdStatusChart.colors"
                                    [dataLabels]="pdStatusChart.dataLabels"
                                    [plotOptions]="pdStatusChart.plotOptions"
                                    [stroke]="pdStatusChart.stroke"
                                    [legend]="pdStatusChart.legend"
                                    [tooltip]="pdStatusChart.tooltip">
                                </apx-chart>
                                <div *ngIf="isLoading" class="h-[300px] flex items-center justify-center text-gray-400 text-sm">Loading...</div>
                            </div>
                            <div class="w-1/2 pl-4">
                                <div class="space-y-4">
                                    <div class="flex items-center">
                                        <div class="w-4 h-4 rounded-sm mr-3 shrink-0" style="background-color: #5CB85C;"></div>
                                        <div>
                                            <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Completed ({{ pdStatusCompletedPct }}%)
                                            </div>
                                            <div class="text-2xl font-bold text-gray-900 dark:text-white">
                                                {{ pdStatusCompleted.toLocaleString() }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="border-t border-gray-200 dark:border-gray-600"></div>
                                    <div class="flex items-center">
                                        <div class="w-4 h-4 rounded-sm mr-3 shrink-0" style="background-color: #FFD700;"></div>
                                        <div>
                                            <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                In Progress ({{ pdStatusInProgressPct }}%)
                                            </div>
                                            <div class="text-2xl font-bold text-gray-900 dark:text-white">
                                                {{ pdStatusInProgress.toLocaleString() }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="border-t border-gray-200 dark:border-gray-600"></div>
                                    <div class="flex items-center">
                                        <div class="w-4 h-4 rounded-sm mr-3 shrink-0" style="background-color: #34495E;"></div>
                                        <div>
                                            <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Expired ({{ pdStatusExpiredPct }}%)
                                            </div>
                                            <div class="text-2xl font-bold text-gray-900 dark:text-white">
                                                {{ pdStatusExpired.toLocaleString() }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </app-card-outer>
                </div>
                }
            </div>

            <!-- Agent Performance Table -->
            @if (canViewAgentPerformance) {
            <div class="mt-4">
                <app-card-outer>
                    <app-card-title
                        [cardHead]="'Agent Performance Overview'"
                        [headingIcon]="'users'"
                        [showPeriodSelect]="true"
                        [period]="selectedPeriod"
                        [periodOptions]="agentPerformancePeriodOptions" />
                    <div>
                        <div class="table-responsive">
                            <table>
                                <thead class="border-b border-gray-200 dark:border-white/10 border-dashed">
                                    <tr>
                                        <th>Agent</th>
                                        <th>Code</th>
                                        <th>Status</th>
                                        <th>Accuracy</th>
                                        <th>Total PDs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngIf="isLoading">
                                        <td colspan="5" class="text-center text-gray-400 py-6">Loading...</td>
                                    </tr>
                                    <tr *ngIf="!isLoading && agentPerformance.length === 0">
                                        <td colspan="5" class="text-center text-gray-400 py-6">No data for the selected period.</td>
                                    </tr>
                                    <tr *ngFor="let agent of pagedAgentPerformance">
                                        <td class="whitespace-nowrap font-medium">
                                            <a [routerLink]="['/manage-agent', agent.uuid]"
                                                class="text-primary hover:underline dark:text-white">{{ agent.name }}</a>
                                        </td>
                                        <td class="whitespace-nowrap text-xs text-gray-500">{{ agent.agent_code ?? '—' }}</td>
                                        <td class="whitespace-nowrap">
                                            <span class="inline-flex items-center gap-1.5 p-1 px-2 rounded-md"
                                                [ngClass]="{
                                                    'text-success bg-success-light': agent.status === 'ACTIVE',
                                                    'text-secondary bg-secondary-light': agent.status !== 'ACTIVE'
                                                }">
                                                <span class="h-1.5 w-1.5 shrink-0 rounded-full"
                                                    [ngClass]="{ 'bg-success': agent.status === 'ACTIVE', 'bg-secondary': agent.status !== 'ACTIVE' }"
                                                    aria-hidden="true"></span>
                                                {{ agent.status }}
                                            </span>
                                        </td>
                                        <td class="whitespace-nowrap">{{ agent.accuracy }}</td>
                                        <td>{{ agent.total_pds }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <app-list-module-pagination
                            *ngIf="!isLoading && agentPerformance.length"
                            [meta]="agentPaginationMeta"
                            [rowCount]="pagedAgentPerformance.length"
                            [loading]="isLoading"
                            (pageChange)="onAgentPageChange($event)" />
                    </div>
                </app-card-outer>
            </div>
            }
        </div>

        <!-- Recent Activity Panel -->
        @if (canViewRecentActivity) {
        <div class="xl:col-span-3">
            <div class="panel p-4 h-full rounded-3xl bg-[#303030] dark:bg-black/60 text-white duration-200">
                <div class="flex items-center justify-between gap-2 pb-2 mb-2 border-b border-gray-700 dark:border-white/10">
                    <h5 class="text-base font-normal dark:text-white">Recent Activity</h5>
                    <a routerLink="/activities" class="shrink-0 text-xs font-semibold text-white/70 hover:text-white hover:underline">View all</a>
                </div>
                <!-- Capped and scrolled: the feed grows with the tenant, and an uncapped panel
                     stretched the row far past the table beside it. -->
                <div class="max-h-[52rem] overflow-y-auto pr-1">
                    <ng-container *ngIf="!isLoading && recentActivity.length > 0">
                        <app-birthday-card
                            *ngFor="let item of recentActivity"
                            [userName]="item.customer_name"
                            [discription]="item.agent_name + ' — ' + item.status + (item.result ? ' / ' + item.result : '')"
                            [imageUrl]="'/assets/images/message@2x.png'"
                            [datePost]="(item.created_at | date:'yyyy/MM/dd,HH:mm') ?? ''" />
                    </ng-container>
                    <ng-container *ngIf="isLoading">
                        <div class="text-gray-400 text-sm py-4 text-center">Loading...</div>
                    </ng-container>
                    <ng-container *ngIf="!isLoading && recentActivity.length === 0">
                        <div class="text-gray-400 text-sm py-4 text-center">No recent activity.</div>
                    </ng-container>
                </div>
            </div>
        </div>
        }
    </div>
</div>
